import api from "../../utils/api";
import WithdrawAcConstant from "@/mixins/withdraw/const/withdrawAcConstant";

export default {
  namespaced: true,

  state: () => ({
    canWithdraw: false,
    reasons: [],
    withdrawReason: "",
    isLoading: false,
    error: null,
    // 설문 관련 상태
    expectation: "",
    expectationText: "",
    selectedReasons: [],
    reasonDetails: {},
    // 탈퇴 신청 정보
    withdrawalRequestDate: "",
    preflight: null,
    idempotencyKey: "",
    noticeAgreed: false,
  }),

  mutations: {
    setWithdrawCheck(state, { canWithdraw, reasons }) {
      state.canWithdraw = canWithdraw;
      state.reasons = reasons || [];
    },
    setWithdrawReason(state, reason) {
      state.withdrawReason = reason;
    },
    setLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setError(state, error) {
      state.error = error;
    },
    setExpectation(state, { expectation, expectationText }) {
      state.expectation = expectation;
      state.expectationText = expectationText || "";
    },
    setSelectedReasons(state, reasons) {
      state.selectedReasons = reasons || [];
    },
    setReasonDetails(state, details) {
      state.reasonDetails = details || {};
    },
    setWithdrawalRequestDate(state, date) {
      state.withdrawalRequestDate = date || "";
    },
    setPreflight(state, preflight) {
      state.preflight = preflight || null;
    },
    setIdempotencyKey(state, key) {
      state.idempotencyKey = key || "";
    },
    setNoticeAgreed(state, agreed) {
      state.noticeAgreed = agreed === true;
    },
    resetState(state) {
      state.canWithdraw = false;
      state.reasons = [];
      state.withdrawReason = "";
      state.error = null;
      state.expectation = "";
      state.expectationText = "";
      state.selectedReasons = [];
      state.reasonDetails = {};
      state.withdrawalRequestDate = "";
      state.preflight = null;
      state.idempotencyKey = "";
      state.noticeAgreed = false;
    },
  },

  actions: {
    // V2가 열려 있으면 중지 예정 서비스를 조회하고, 닫혀 있으면 기존 V1 판정을 유지한다.
    async acPrepareWithdrawal({ commit, dispatch }) {
      commit("setLoading", true);
      try {
        const res = await api.post(WithdrawAcConstant.POST_WITHDRAWAL_PREFLIGHT, {});
        const data = res.data?.data || res.data || {};
        commit("setPreflight", data);
        commit("setWithdrawCheck", { canWithdraw: true, reasons: data.services || [] });
        commit("setIdempotencyKey", createIdempotencyKey());
        return data;
      } catch (error) {
        const code = error?.response?.data?.code;
        if (code === 5607 || error?.response?.status === 404) {
          commit("setPreflight", null);
          const legacyResult = await dispatch("acCheckWithdrawal");
          return { ...legacyResult, flowVersion: "V1" };
        }
        commit("setError", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },
    async acCancelDobyPayment({ commit, dispatch }, itemId) {
      if (typeof itemId !== "string" || itemId.trim().length === 0) {
        return {
          success: false,
          reason: "invalid-item-id",
          code: null,
          status: null,
          refreshFailed: false,
          restartFromNotice: false,
        };
      }

      try {
        const res = await api.post(WithdrawAcConstant.POST_WITHDRAWAL_DOBY_CANCELLATION, {
          itemId,
        });
        const data = res.data?.data || res.data || {};
        try {
          const refreshedPreflight = await dispatch("acPrepareWithdrawal");
          const isV2 = refreshedPreflight?.flowVersion === "V2";
          const hasDisplayItems = Array.isArray(refreshedPreflight?.displayItems);
          const targetStillExists = refreshedPreflight?.displayItems?.some(
            item => item?.itemId === itemId,
          );

          // 결제 취소 뒤 V2 목록에서 대상이 사라진 것까지 확인해야 다음 단계로 진행할 수 있다.
          if (!isV2 || !hasDisplayItems || targetStillExists) {
            return {
              success: false,
              data,
              reason: !isV2 ? "flow-version-changed" : "preflight-not-refreshed",
              code: null,
              status: null,
              refreshFailed: true,
              restartFromNotice: true,
            };
          }
          return { success: true, data, refreshFailed: false };
        } catch (refreshError) {
          commit("setError", refreshError);
          return {
            success: false,
            data,
            reason: "preflight-refresh-failed",
            code: null,
            status: null,
            refreshFailed: true,
            restartFromNotice: true,
          };
        }
      } catch (error) {
        commit("setError", error);
        const code = error?.response?.data?.code || null;
        const status = error?.response?.status || null;

        // PG 취소는 웹훅으로 최종 확정된다. 승인 직후(5614) 또는 웹훅 완료 뒤
        // 이전 itemId로 재시도한 경우(5613), fresh preflight가 정본이다.
        if (code === 5613 || code === 5614) {
          try {
            const refreshedPreflight = await dispatch("acPrepareWithdrawal");
            const isV2 = refreshedPreflight?.flowVersion === "V2";
            const hasDisplayItems = Array.isArray(refreshedPreflight?.displayItems);
            const targetStillExists = refreshedPreflight?.displayItems?.some(
              item => item?.itemId === itemId,
            );

            if (isV2 && hasDisplayItems && !targetStillExists) {
              return {
                success: true,
                data: null,
                recoveredFromRefresh: true,
                refreshFailed: false,
              };
            }
            if (isV2 && hasDisplayItems && targetStillExists && code === 5614) {
              return {
                success: false,
                reason: "cancellation-processing",
                code,
                status,
                processing: true,
                refreshFailed: false,
                restartFromNotice: false,
              };
            }
            return {
              success: false,
              reason: !isV2 ? "flow-version-changed" : "cancellation-state-mismatch",
              code,
              status,
              refreshFailed: true,
              restartFromNotice: true,
            };
          } catch (refreshError) {
            commit("setError", refreshError);
            return {
              success: false,
              reason: "preflight-refresh-failed",
              code,
              status,
              refreshFailed: true,
              restartFromNotice: true,
            };
          }
        }
        return {
          success: false,
          reason: "cancellation-failed",
          code,
          status,
          refreshFailed: false,
          restartFromNotice: false,
        };
      }
    },
    // 탈퇴 가능 여부 조회
    async acCheckWithdrawal({ commit }) {
      commit("setLoading", true);
      try {
        const res = await api.get(WithdrawAcConstant.GET_WITHDRAWAL_CHECK);
        const data = res.data?.data || res.data || {};

        commit("setWithdrawCheck", {
          canWithdraw: data.canWithdraw ?? false,
          reasons: data.reasons || [],
        });
        return {
          canWithdraw: data.canWithdraw ?? false,
          reasons: data.reasons || [],
        };
      } catch (error) {
        commit("setError", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    // 탈퇴 사유 저장
    acSetWithdrawReason({ commit }, reason) {
      commit("setWithdrawReason", reason);
    },

    // 기대사항 저장
    acSetExpectation({ commit }, { expectation, expectationText }) {
      commit("setExpectation", { expectation, expectationText });
    },

    // 해지 이유 저장
    acSetReasons({ commit }, { reasons, reasonDetails }) {
      commit("setSelectedReasons", reasons);
      commit("setReasonDetails", reasonDetails);
    },

    // 탈퇴 설문 제출
    async acSubmitSurvey({ commit, state }) {
      try {
        await api.post(WithdrawAcConstant.POST_WITHDRAWAL_SURVEY, {
          expectation: state.expectation,
          expectationText: state.expectationText || null,
          reasons: state.selectedReasons,
          reasonDetails: Object.keys(state.reasonDetails).length > 0 ? state.reasonDetails : null,
        });
        return true;
      } catch (error) {
        commit("setError", error);
        return false;
      }
    },

    // 탈퇴 사유 코멘트 전송
    async acSubmitComment({ commit, state }) {
      try {
        await api.post(WithdrawAcConstant.POST_WITHDRAWAL_COMMENT, {
          comment: state.withdrawReason,
        });
        return true;
      } catch (error) {
        commit("setError", error);
        return false;
      }
    },

    // 탈퇴 실행
    async acExecuteWithdrawal({ commit, state, dispatch }) {
      try {
        if (state.preflight?.flowVersion === "V2") {
          const preflight = state.preflight;
          const idempotencyKey = state.idempotencyKey || createIdempotencyKey();
          if (!state.idempotencyKey) commit("setIdempotencyKey", idempotencyKey);
          const body = {
            flowVersion: preflight.flowVersion,
            stateDigest: preflight.stateDigest,
            consentVersion: preflight.consentVersion,
            acknowledgedActionIds: (preflight.services || []).map(service => service.actionId),
          };
          const res = await api.post(WithdrawAcConstant.POST_WITHDRAWAL_CONFIRM, body, {
            headers: { "Idempotency-Key": idempotencyKey },
          });
          const data = res.data?.data || res.data;
          if (data?.withdrawalRequestDate) {
            commit("setWithdrawalRequestDate", data.withdrawalRequestDate);
          }
          return { success: true, data };
        }
        await api.post(WithdrawAcConstant.POST_WITHDRAWAL_EXECUTE, "");
        return { success: true };
      } catch (error) {
        if (error?.response?.data?.code === 5603) {
          try {
            await dispatch("acPrepareWithdrawal");
          } catch (_) {
            // 원래 confirm 오류를 사용자에게 전달하기 위해 준비 재시도 오류는 삼킨다.
          }
          return { success: false, retryPreflight: true };
        }
        commit("setError", error);
        return { success: false };
      }
    },

    async acFinalizeWithdrawal({ dispatch }) {
      const surveySaved = await dispatch("acSubmitSurvey");
      if (!surveySaved) return { success: false, reason: "survey-failed" };
      return await dispatch("acExecuteWithdrawal");
    },

    // 탈퇴 취소
    async acCancelWithdrawal({ commit }) {
      try {
        const res = await api.patch(WithdrawAcConstant.PATCH_WITHDRAWAL_CANCEL);
        return { success: true, data: res.data?.data || res.data || null };
      } catch (error) {
        commit("setError", error);
        return {
          success: false,
          code: error?.response?.data?.code || null,
          status: error?.response?.status || null,
          message: error?.response?.data?.message || "",
        };
      }
    },

    // 탈퇴 불가 상태에서 탈퇴 요청 (이력 저장 + 상태 변경)
    async acSubmitBlockRequest({ commit, state }) {
      try {
        const reasons = state.reasons.map(r => r.type);
        await api.post(WithdrawAcConstant.POST_WITHDRAWAL_BLOCK_REQUEST, {
          reasons: reasons,
        });
        return true;
      } catch (error) {
        commit("setError", error);
        return false;
      }
    },

    // 탈퇴 신청 정보 조회 (pending 상태)
    async acGetWithdrawalInfo({ commit }) {
      try {
        const res = await api.get(WithdrawAcConstant.GET_WITHDRAWAL_CHECK);
        const data = res.data?.data || res.data || {};

        if (data.withdrawalRequestDate) {
          commit("setWithdrawalRequestDate", data.withdrawalRequestDate);
        }
        return data;
      } catch (error) {
        commit("setError", error);
        return null;
      }
    },

    // 상태 초기화
    acResetState({ commit }) {
      commit("resetState");
    },
  },

  getters: {
    isWithdrawAllowed: state => state.canWithdraw,
    withdrawReasons: state => state.reasons,
    isLoading: state => state.isLoading,
    getExpectation: state => state.expectation,
    getExpectationText: state => state.expectationText,
    getSelectedReasons: state => state.selectedReasons,
    getReasonDetails: state => state.reasonDetails,
    getPreflight: state => state.preflight,
  },
};

function createIdempotencyKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
}
